import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { SEND_FODBOLDHOLD_NOTICES } from '../../graphql/mutations'
import { initialsFrom, nextMatch } from './logic'

const noticeDialogPaper = {
  className: 'hold-create-paper hold-notice-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const noticeDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

function clip(text) {
  const value = String(text || '').replace(/\s+/g, ' ').trim()
  return value.length > 180 ? `${value.slice(0, 177)}…` : value
}

function noticeDay(iso) {
  return new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'long' }).format(new Date(iso))
}

function owedFine(season, mateId) {
  const fines = (season.fines || []).filter((fine) => fine.mateId === mateId)
  const given = fines.reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0)
  const recorded = (season.finePayments || []).find((item) => item.mateId === mateId)
  const paidRaw = recorded
    ? recorded.amount
    : fines.reduce((sum, fine) => sum + (fine.paid ? fine.amount : 0), 0)
  return given - Math.max(0, paidRaw || 0)
}

function upcomingEvent(season) {
  const now = Date.now()
  return (season.events || [])
    .filter((event) => event.status !== 'cancelled' && new Date(event.kickoff).getTime() >= now)
    .sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))[0] || null
}

function hasReplied(item) {
  return new Set([
    ...(item.attendees || []),
    ...(item.waitlist || []),
    ...(item.declined || []),
  ])
}

function notSignedUp(members, item) {
  const gone = hasReplied(item)
  return members.filter((member) => !gone.has(member.id)).map((member) => member.id)
}

function noticeIdeas(team, season) {
  const ideas = []
  const members = team.members || []
  const match = nextMatch(season)
  if (match) {
    const missing = notSignedUp(members, match)
    const open = match.max == null || match.attendees.length < match.max
    if (missing.length) {
      ideas.push({
        id: `match-${match.id}`,
        label: 'Kamp',
        replyOnly: true,
        notices: [{
          mateIds: missing,
          body: open
            ? clip(`Der er stadig pladser tilbage til kampen d. ${noticeDay(match.kickoff)} mod ${match.opponent}.`)
            : clip(`Vi mangler dit svar til kampen d. ${noticeDay(match.kickoff)} mod ${match.opponent}.`),
          url: `/hold/${team.id}/kamp/${match.id}`,
        }],
      })
    }
  }

  if ((season.fee?.amount || 0) > 0) {
    const missing = members.filter((member) => !season.fee.paid[member.id]).map((member) => member.id)
    if (missing.length) {
      ideas.push({
        id: 'fee',
        label: 'Kontingent',
        notices: [{
          mateIds: missing,
          body: 'Du mangler at betale kontingent.',
          url: `/hold/${team.id}/kontingent`,
        }],
      })
    }
  }

  const fines = members.map((member) => {
    const owed = owedFine(season, member.id)
    if (owed <= 0) return null
    return {
      mateIds: [member.id],
      body: `Du skylder ${owed} kr. til bødekassen.`,
      url: `/hold/${team.id}/boedekasse`,
    }
  }).filter(Boolean)
  ideas.push({
    id: 'fines',
    label: 'Bødekasse',
    personal: true,
    notices: fines,
    empty: 'Ingen skylder noget i bødekassen lige nu.',
  })

  const event = upcomingEvent(season)
  if (event) {
    const full = event.max != null && (event.attendees || []).length >= event.max
    const missing = notSignedUp(members, event)
    if (!full && missing.length) {
      ideas.push({
        id: `event-${event.id}`,
        label: 'Arrangement',
        replyOnly: true,
        notices: [{
          mateIds: missing,
          body: clip(`Husk at melde dig til ${event.title}.`),
          url: `/hold/${team.id}/arrangement/${event.id}`,
        }],
      })
    }
  }

  return ideas
}

function gqlError(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || 'Beskeden kunne ikke sendes'
}

export default function NoticeDialog({ open, team, season, nameOf, onClose, onSent, onError }) {
  const ideas = useMemo(() => (team && season ? noticeIdeas(team, season) : []), [team, season])
  const [step, setStep] = useState(0)
  const [ideaId, setIdeaId] = useState('custom')
  const [body, setBody] = useState('')
  const [picked, setPicked] = useState(() => new Set((team?.members || []).map((member) => member.id)))
  const [busy, setBusy] = useState(false)
  const [sendNotices] = useMutation(SEND_FODBOLDHOLD_NOTICES)
  const idea = ideas.find((item) => item.id === ideaId) || null
  const members = team?.members || []
  const replyIds = idea?.replyOnly
    ? new Set(idea.notices.flatMap((notice) => notice.mateIds))
    : null

  const choose = (id) => {
    setIdeaId(id)
    const next = ideas.find((item) => item.id === id)
    if (!next) {
      setBody('')
      setPicked(new Set(members.map((member) => member.id)))
      return
    }
    setBody(next.personal ? '' : next.notices[0].body)
    setPicked(new Set(next.notices.flatMap((notice) => notice.mateIds)))
  }

  const toggle = (id) => {
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const notices = () => {
    if (idea?.personal) {
      return idea.notices
        .map((notice) => ({ ...notice, mateIds: notice.mateIds.filter((id) => picked.has(id)) }))
        .filter((notice) => notice.mateIds.length)
    }
    const text = body.trim()
    const mateIds = [...picked].filter((id) => !replyIds || replyIds.has(id))
    if (!text || mateIds.length === 0) return []
    return [{
      mateIds,
      body: text,
      url: idea?.notices[0]?.url || `/hold/${team.id}`,
    }]
  }

  const personalLine = (memberId) => (
    idea?.personal ? idea.notices.find((notice) => notice.mateIds.includes(memberId))?.body : ''
  )
  const visible = members.filter((member) => {
    if (idea?.personal && !personalLine(member.id)) return false
    if (replyIds && !replyIds.has(member.id)) return false
    return true
  })
  const allOn = visible.length > 0 && visible.every((member) => picked.has(member.id))
  const ready = notices()
  const route = ['Type', 'Besked', 'Modtagere']
  const stepCopy = step === 0
    ? 'Vælg hvilken besked du vil sende.'
    : step === 1
      ? (idea?.personal ? 'Hver person får sit eget beløb.' : 'Skriv beskeden.')
      : 'Vælg hvem der skal have beskeden.'
  const canNext = step === 0
    ? ideaId === 'custom' || Boolean(idea?.notices.length)
    : Boolean(body.trim()) || Boolean(idea?.personal && idea.notices.length)

  const send = async () => {
    const payload = notices()
    if (!payload.length || busy) return
    setBusy(true)
    try {
      const result = await sendNotices({ variables: { teamId: team.id, notices: payload } })
      const sent = result.data?.sendFodboldholdNotices?.sent || 0
      const failed = result.data?.sendFodboldholdNotices?.failed || 0
      if (sent === 0) {
        onError(failed > 0
          ? 'Ingen modtog beskeden. De har ikke appen på hjemmeskærmen.'
          : 'Ingen modtog beskeden.')
        return
      }
      onSent(failed > 0
        ? `Beskeden er sendt til ${sent}. ${failed} kunne ikke nås.`
        : `Beskeden er sendt til ${sent}.`)
      onClose()
    } catch (error) {
      onError(gqlError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={noticeDialogPaper}
      BackdropProps={noticeDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Besked</DialogTitle>
      <DialogContent>
        <form className="hold-form hold-create-form hold-give-route-form" onSubmit={(event) => event.preventDefault()}>
          <ol className="hold-give-route">
            {route.map((label, index) => (
              <li key={label} className={index < step ? 'is-done' : ''}>
                <button
                  type="button"
                  className={index === step ? 'is-on' : index < step ? 'is-done' : ''}
                  disabled={index > step || busy}
                  onClick={() => setStep(index)}
                >
                  <span>{index + 1}</span>
                  {label}
                </button>
              </li>
            ))}
          </ol>
          <div className="hold-give-route-body">
            <p className="hold-copy">{stepCopy}</p>
            {step === 0 ? (
              <ul className="hold-give-list">
                <li>
                  <button
                    type="button"
                    className={`hold-give-pick ${ideaId === 'custom' ? 'is-on' : ''}`}
                    aria-pressed={ideaId === 'custom'}
                    onClick={() => choose('custom')}
                  >
                    <span>Skriv selv</span>
                  </button>
                </li>
                {ideas.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`hold-give-pick ${ideaId === item.id ? 'is-on' : ''}`}
                      aria-pressed={ideaId === item.id}
                      onClick={() => choose(item.id)}
                    >
                      <span>{item.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {step === 0 && idea && idea.notices.length === 0 ? <p className="hold-note">{idea.empty}</p> : null}
            {step === 1 ? (
              idea?.personal ? (
                idea.notices.length ? null : <p className="hold-note">{idea.empty}</p>
              ) : (
                <label>
                  Besked
                  <textarea
                    value={body}
                    maxLength={180}
                    rows={4}
                    placeholder="Skriv beskeden"
                    onChange={(event) => setBody(event.target.value)}
                  />
                </label>
              )
            ) : null}
            {step === 2 ? (
              <>
                {visible.length > 1 ? (
                  <div className="hold-give-people-head">
                    <span />
                    <button
                      type="button"
                      className="hold-text-button"
                      onClick={() => setPicked(allOn ? new Set() : new Set(visible.map((member) => member.id)))}
                    >
                      {allOn ? 'Fravælg alle' : 'Vælg alle'}
                    </button>
                  </div>
                ) : null}
                {visible.length === 0 ? <p className="hold-empty">Ingen modtagere.</p> : (
                  <ul className="hold-give-list">
                    {visible.map((member) => {
                      const on = picked.has(member.id)
                      const person = nameOf(member.id)
                      const line = personalLine(member.id)
                      return (
                        <li key={member.id}>
                          <button
                            type="button"
                            className={`hold-give-pick ${on ? 'is-on' : ''}`}
                            aria-pressed={on}
                            onClick={() => toggle(member.id)}
                          >
                            <span className="hold-fine-avatar hold-give-avatar" aria-hidden="true">{initialsFrom(person)}</span>
                            <span className="hold-give-name">
                              {person}
                              {line ? <em>{line}</em> : null}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </>
            ) : null}
          </div>
          <div className="hold-give-nav">
            {step > 0 ? (
              <button type="button" className="hold-action hold-action--ghost" disabled={busy} onClick={() => setStep(step - 1)}>
                Tilbage
              </button>
            ) : null}
            {step < 2 ? (
              <button type="button" className="hold-action hold-action--save" disabled={!canNext || busy} onClick={() => setStep(step + 1)}>
                Næste
              </button>
            ) : (
              <button type="button" className="hold-action hold-action--save" disabled={!ready.length || busy} onClick={send}>
                {busy ? 'Sender…' : 'Send besked'}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
